import { useEffect, useState } from 'react';
import { CloudSun, MapPin, X } from 'lucide-react';
import { Store } from './mirror-store.js';
import { mirrorApiFetch } from './ai.js';

const WEATHER_CODES = {
  0: 'Clear', 1: 'Mostly clear', 2: 'Partly cloudy', 3: 'Overcast',
  45: 'Fog', 48: 'Fog', 51: 'Drizzle', 53: 'Drizzle', 55: 'Drizzle',
  61: 'Rain', 63: 'Rain', 65: 'Heavy rain', 71: 'Snow', 73: 'Snow', 75: 'Heavy snow',
  80: 'Showers', 81: 'Showers', 82: 'Heavy showers', 95: 'Thunderstorm', 96: 'Thunderstorm', 99: 'Thunderstorm',
};

async function fetchTempForCoords(latitude, longitude) {
  return mirrorApiFetch(`/api/weather?latitude=${encodeURIComponent(latitude)}&longitude=${encodeURIComponent(longitude)}`);
}

async function fetchTempForZip(zip) {
  return mirrorApiFetch(`/api/weather?zip=${encodeURIComponent(zip)}`);
}

function getCurrentPosition() {
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      reject(new Error('Location is not available on this device.'));
      return;
    }
    navigator.geolocation.getCurrentPosition(
      position => resolve(position.coords),
      error => {
        if (error?.code === 1) reject(new Error('Location permission was denied. Enable Location for Safari in device settings, or use your zip code.'));
        else if (error?.code === 2) reject(new Error('Your location could not be determined. Try your zip code instead.'));
        else reject(new Error('Location lookup timed out. Try your zip code instead.'));
      },
      { timeout: 10000 },
    );
  });
}

function WeatherPicker({ open, onClose, onResolved, onReading }) {
  const [zip, setZip] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (open) { setZip(''); setBusy(false); setError(''); }
  }, [open]);

  if (!open) return null;

  const useMyLocation = async () => {
    setBusy(true);
    setError('');
    try {
      const coords = await getCurrentPosition();
      const reading = await fetchTempForCoords(coords.latitude, coords.longitude);
      Store.updatePreferences({ weatherSource: 'geolocation', weatherZip: '' });
      onResolved(reading);
      onReading?.(reading);
      onClose();
    } catch (err) {
      setError(err.message || 'Could not get your location.');
    } finally {
      setBusy(false);
    }
  };

  const submitZip = async event => {
    event.preventDefault();
    const trimmed = zip.trim().replace(/\s+/g, '');
    if (!trimmed) return;
    if (!/^\d{5}(?:-\d{4})?$/.test(trimmed)) {
      setError('Enter a valid five-digit US zip code.');
      return;
    }
    setBusy(true);
    setError('');
    try {
      const reading = await fetchTempForZip(trimmed);
      Store.updatePreferences({ weatherSource: 'zip', weatherZip: trimmed });
      onResolved(reading);
      onReading?.(reading);
      onClose();
    } catch (err) {
      setError(err.message || 'Could not find that zip code.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="sheet-backdrop" role="presentation" onMouseDown={event => event.target === event.currentTarget && onClose()}>
      <form className="capture-sheet weather-sheet" role="dialog" aria-modal="true" aria-labelledby="weather-sheet-title" onSubmit={submitZip}>
        <div className="sheet-handle" />
        <div className="sheet-title">
          <div><span>Weather</span><h2 id="weather-sheet-title">Today's temperature.</h2></div>
          <button type="button" onClick={onClose} aria-label="Close"><X /></button>
        </div>
        <p className="surrounding-sheet-note">Use your location, or enter a zip code to see today's temperature.</p>
        <button type="button" className="weather-location-button" disabled={busy} onClick={useMyLocation}>
          <MapPin /><span>{busy ? 'Locating…' : 'Use my location'}</span>
        </button>
        <label className="setup-field"><span>Or enter a zip code</span><input value={zip} onChange={event => setZip(event.target.value)} placeholder="e.g. 90210" inputMode="numeric" maxLength="10" /></label>
        {error ? <p className="weather-error">{error}</p> : null}
        <button type="submit" className="save-capture" disabled={busy || !zip.trim()}>Save zip code</button>
      </form>
    </div>
  );
}

export default function WeatherWidget({ preferences, className = '', onReading }) {
  const [reading, setReading] = useState(null);
  const [pickerOpen, setPickerOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        if (preferences?.weatherSource === 'geolocation') {
          const coords = await getCurrentPosition();
          if (cancelled) return;
          const nextReading = await fetchTempForCoords(coords.latitude, coords.longitude);
          setReading(nextReading);
          onReading?.(nextReading);
        } else if (preferences?.weatherSource === 'zip' && preferences.weatherZip) {
          const result = await fetchTempForZip(preferences.weatherZip);
          if (cancelled) return;
          setReading(result);
          onReading?.(result);
        }
      } catch {
        if (!cancelled) setReading(null);
      }
    })();
    return () => { cancelled = true; };
  }, [onReading, preferences?.weatherSource, preferences?.weatherZip]);

  return (
    <>
      <button type="button" className={`weather-widget ${className}`} onClick={() => setPickerOpen(true)} aria-label="Set weather location">
        <CloudSun />
        {reading ? <span>{reading.temp}°</span> : <span>Weather</span>}
      </button>
      <WeatherPicker open={pickerOpen} onClose={() => setPickerOpen(false)} onResolved={setReading} onReading={onReading} />
    </>
  );
}
